// Libraries
import axios, { AxiosError, AxiosInstance, AxiosResponse, CancelTokenSource } from "axios";
import axiosRetry from "axios-retry";

// Services & Helpers
import { projectAuth } from "@/firebase/config";
import { sentryService } from "@/core/services/sentry/sentryService";

// Stores
import { useUserStore } from "@/store/user";

// Types
import { ApiErrorCode, ApiError } from "@/@types/api";

export type ErrorStatus = ApiErrorCode | undefined;

type RequestT = {
  endpoint: string;
  body?: Record<string, unknown>;
};

type CallT = RequestT & {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  signal?: AbortSignal;
  requestId?: string;
};

class ApiService {
  private instance: AxiosInstance;
  private token: string | null = null;
  private abortControllers = new Map<string, AbortController>();

  constructor(client: AxiosInstance) {
    this.instance = client;
    this.intercept();
    this.setupRetryLogic();
  }

  async get<T>({ endpoint }: Omit<RequestT, "body">, requestId?: string) {
    return this.send<T>({ method: "GET", endpoint, requestId });
  }

  async post<T>(data: RequestT, requestId?: string) {
    return this.send<T>({ method: "POST", ...data, requestId });
  }

  async put<T>(data: RequestT, requestId?: string) {
    return this.send<T>({ method: "PUT", ...data, requestId });
  }

  async patch<T>(data: RequestT, requestId?: string) {
    return this.send<T>({ method: "PATCH", ...data, requestId });
  }

  async delete<T>(data: RequestT, requestId?: string) {
    return this.send<T>({ method: "DELETE", ...data, requestId });
  }

  private async send<T>(
    call: CallT,
    retry: boolean = true
  ): Promise<{ data: T | null; error: ErrorStatus | null; errorData: ApiError | null }> {
    const startTime = Date.now();
    
    // Gerenciar AbortController para cancelamento
    if (call.requestId) {
      // Cancelar requisição anterior com mesmo ID se existir
      const existingController = this.abortControllers.get(call.requestId);
      if (existingController) {
        existingController.abort();
        this.abortControllers.delete(call.requestId);
      }
      
      // Criar novo controller para esta requisição
      const newController = new AbortController();
      this.abortControllers.set(call.requestId, newController);
      call.signal = newController.signal;
    }
    
    try {
      sentryService.addBreadcrumb("api", `${call.method} ${call.endpoint}`, {
        method: call.method,
        endpoint: call.endpoint,
        hasBody: !!call.body,
        startTime: new Date(startTime).toISOString()
      });

      const data = await this.fetch<T>(call);
      
      // Limpar AbortController após sucesso
      if (call.requestId) {
        this.abortControllers.delete(call.requestId);
      }
      
      const duration = Date.now() - startTime;
      sentryService.addBreadcrumb("api", `Success: ${call.method} ${call.endpoint}`, {
        duration: `${duration}ms`,
        status: "success"
      });

      return { data, error: null, errorData: null };
    } catch (error) {
      // Limpar AbortController em caso de erro
      if (call.requestId) {
        this.abortControllers.delete(call.requestId);
      }
      
      // Não capturar erros de cancelamento no Sentry
      if (axios.isCancel(error)) {
        return { data: null, error: "request-cancelled" as ApiErrorCode, errorData: null };
      }
      
      const auth = error instanceof AxiosError && error.response?.status === 401;

      if (auth && retry) {
        await this.refresh();
        return this.send<T>(call, false);
      }

      if (error instanceof AxiosError) {
        // Tratamento especial para erros de certificado SSL
        if (error.code === 'ERR_CERT_AUTHORITY_INVALID' || 
            error.code === 'UNABLE_TO_VERIFY_LEAF_SIGNATURE' ||
            error.message?.includes('certificate')) {
          
          sentryService.addBreadcrumb("api", "SSL Certificate Error", {
            endpoint: call.endpoint,
            errorCode: error.code,
            message: error.message,
            suggestion: "Possível proxy corporativo ou firewall interferindo"
          });
          
          // Retornar erro mais amigável para o usuário
          return { 
            data: null, 
            error: "ssl-certificate-error" as ApiErrorCode, 
            errorData: {
              error: "ssl-certificate-error",
              message: "Erro de conexão segura. Se você está em uma rede corporativa, consulte o guia de resolução ou contate o suporte de TI.",
              details: {
                originalError: error.code,
                endpoint: call.endpoint
              }
            } as ApiError
          };
        }

        this.captureAxiosError(error, call, startTime);

        return this.treat(error);
      }

      sentryService.captureError(
        error,
        "unexpected-error",
        { title: "Erro Inesperado", message: "Ocorreu um erro inesperado ao processar a requisição" },
        { endpoint: call.endpoint, method: call.method || "unknown" }
      );

      return { data: null, error: "unexpected-error", errorData: null };
    }
  }

  private async fetch<T>({ method, endpoint, body, signal }: CallT) {
    const response: AxiosResponse<T> = await this.instance({ 
      method, 
      url: endpoint, 
      data: body,
      signal 
    });
    return response.data;
  }

  private async treat(error: AxiosError<ApiError>) {
    return { data: null, error: this.handleError(error), errorData: error.response?.data ?? null };
  }

  async refresh() {
    const token = await projectAuth.currentUser?.getIdToken();

    if (!token) return await this.redirect();

    this.token = token;
  }

  private async redirect() {
    const userStore = useUserStore();

    await userStore.logout();
    window.location.href = "/login";
  }

  intercept(token?: string) {
    this.token = token ?? this.token;

    this.instance.interceptors.request.use(async config => {
      config.headers.Authorization = `Bearer ${this.token}`;

      return config;
    });
  }
  
  private setupRetryLogic() {
    // Configurar retry logic com axios-retry
    axiosRetry(this.instance, {
      retries: 2, // Número de tentativas
      retryDelay: axiosRetry.exponentialDelay, // Delay exponencial entre tentativas
      retryCondition: (error) => {
        // Retry apenas para erros de rede e métodos idempotentes
        const isNetworkError = axiosRetry.isNetworkError(error);
        const isTimeout = error.code === "ECONNABORTED";
        const isIdempotent = error.config?.method && 
          ["GET", "HEAD", "OPTIONS", "PUT", "DELETE"].includes(error.config.method.toUpperCase());
        
        // Não fazer retry em requisições canceladas
        if (axios.isCancel(error)) {
          return false;
        }
        
        // Fazer retry apenas em erros de rede/timeout para métodos idempotentes
        return (isNetworkError || isTimeout) && isIdempotent;
      },
      onRetry: (retryCount, error, requestConfig) => {
        // Log de retry para debugging
        sentryService.addBreadcrumb("api", `Retry ${retryCount}: ${requestConfig.method} ${requestConfig.url}`, {
          retryCount,
          error: error.message,
          errorCode: error.code
        });
      }
    });
  }
  
  // Método público para cancelar requisições por ID
  cancelRequest(requestId: string) {
    const controller = this.abortControllers.get(requestId);
    if (controller) {
      controller.abort();
      this.abortControllers.delete(requestId);
    }
  }
  
  // Método para cancelar todas as requisições pendentes
  cancelAllRequests() {
    this.abortControllers.forEach(controller => controller.abort());
    this.abortControllers.clear();
  }

  private handleError(error: AxiosError<ApiError>): ApiErrorCode {
    if (error.response && error.response.data.error) {
      return error.response.data.error as ApiErrorCode;
    } else if (error.request) {
      return "network-error";
    } else {
      return "unexpected-error";
    }
  }

  private captureAxiosError(error: AxiosError<ApiError>, call: CallT, startTime?: number) {
    let userInfo:
      | {
          id: string;
          email: string;
          name: string;
          teamId: string;
          projectId: string;
          role: string;
        }
      | undefined;

    try {
      const userStore = useUserStore();
      const currentUser = userStore.userInfos;

      if (currentUser) {
        userInfo = {
          id: currentUser.id,
          email: currentUser.email,
          name: currentUser.firstName + " " + currentUser.lastName,
          teamId: currentUser.activeTeam?.id,
          projectId: currentUser.lastActiveProject?.id,
          role: currentUser.activeTeam?.role
        };
      }
    } catch (e) {
      // NOTE: Silenciar erro se o userStore não estiver disponível
    }

    if (error.response && error.response.status >= 500) {
      sentryService.captureReplay("Erro critico de servidor", {
        endpoint: call.endpoint,
        method: call.method || "unknown",
        status: error.response.status.toString(),
        errorCode: error.response.data?.error || `http-${error.response.status}`,
        requestBody: JSON.stringify(call.body || {})
      });
    }

    const errorWithBody = {
      ...error,
      data: call.body
    };

    if (error.response) {
      sentryService.captureError(
        errorWithBody,
        error.response.data?.error || `http-${error.response.status}`,
        {
          title: `Erro ${error.response.status}`,
          message: error.response.data?.message || error.message
        },
        {
          endpoint: call.endpoint,
          method: call.method || "unknown",
          status: error.response.status.toString(),
          requestBody: JSON.stringify(call.body || {}),
          error_source: "api"
        },
        userInfo
      );
    } else if (error.request) {
      const duration = startTime ? Date.now() - startTime : undefined;
      const isTimeout = error.code === "ECONNABORTED";
      
      sentryService.captureError(
        errorWithBody,
        "network-error",
        {
          title: isTimeout ? "Timeout de Requisição" : "Erro de Rede",
          message: isTimeout 
            ? `A requisição excedeu o tempo limite de ${DEFAULT_TIMEOUT/1000} segundos (${DEFAULT_TIMEOUT/60000} minutos)`
            : "A requisição foi enviada, mas não houve resposta do servidor"
        },
        {
          endpoint: call.endpoint,
          method: call.method || "unknown",
          requestBody: JSON.stringify(call.body || {}),
          error_source: "api",
          duration: duration ? `${duration}ms` : "unknown",
          error_code: error.code || "unknown",
          is_timeout: String(isTimeout),
          network_status: navigator.onLine ? "online" : "offline",
          user_agent: navigator.userAgent,
          connection_type: (navigator as any).connection?.effectiveType || "unknown"
        },
        userInfo
      );
    } else {
      sentryService.captureError(
        errorWithBody,
        "request-config-error",
        { title: "Erro de Configuração", message: error.message },
        {
          endpoint: call.endpoint,
          method: call.method || "unknown",
          requestBody: JSON.stringify(call.body || {}),
          error_source: "api"
        },
        userInfo
      );
    }
  }
}

const baseURL = import.meta.env.VITE_NEW_API_BASE_URL;
const DEFAULT_TIMEOUT = parseInt(import.meta.env.VITE_API_TIMEOUT || "180000"); // 3 minutos padrão

const client = axios.create({ 
  baseURL, 
  timeout: DEFAULT_TIMEOUT,
  headers: { "Content-Type": "application/json" } 
});
const apiService = new ApiService(client);

const multipart = axios.create({ 
  baseURL, 
  timeout: DEFAULT_TIMEOUT, // 3 minutos para uploads também
  headers: { "Content-Type": "multipart/form-data" } 
});
const apiMultipartService = new ApiService(multipart);

export { apiService, apiMultipartService };
